import { useEffect, useRef, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { routePattern, scrubAnalyticsUrls } from '@skupreme/shared/utils/analytics';
import { useAuth } from './auth';
import { useDebouncedEffect } from '../hooks';
import { useGlobalContext } from './global';
import type { IOrganization } from 'types';

interface IIntegrationProviderProps {
  children: ReactNode;
}

const POSTHOG_KEY: string | undefined = import.meta.env.VITE_POSTHOG_KEY;

// `build:production` is the only build that runs with mode `production`
// (warehouse.skupreme.com); staging and local builds never reach the prod project.
const isProductionBuild = (): boolean =>
  typeof window !== 'undefined' && import.meta.env.MODE === 'production';

function initializePostHog() {
  if (!POSTHOG_KEY || !isProductionBuild() || posthog.__loaded) return;

  posthog.init(POSTHOG_KEY, {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    session_recording: { maskAllInputs: true },
    // Unhandled errors and promise rejections become $exception events
    // (PostHog error tracking), next to the session replay they happened in.
    capture_exceptions: true,
    before_send: event => {
      if (!event) return event;
      // Query strings and fragments carry sign-in codes and tokens; never record them.
      scrubAnalyticsUrls(event.properties);
      if (event.event === '$pageview') {
        const pathname =
          typeof event.properties.$pathname === 'string'
            ? event.properties.$pathname
            : window.location.pathname;
        event.properties.route_pattern = routePattern(pathname);
      }
      return event;
    },
  });
  posthog.register({ app: 'warehouse' });
}

/** Sets up PostHog and keeps its user identity in step with who is
 * signed in. No chat here: the support chat widget was retired in EN-3113. */
export function IntegrationProvider({ children }: IIntegrationProviderProps) {
  const { isAuthenticated, user, loading } = useAuth();
  const { currentWarehouse } = useGlobalContext();
  const wasIdentified = useRef(false);

  useEffect(() => {
    initializePostHog();
  }, []);

  const loadPostHogUser = () => {
    if (!user?._id || !posthog.__loaded) return;

    const currentOrg: IOrganization | undefined = user.organizations?.find(
      x => x._id === user.organizationId,
    );

    posthog.identify(user._id, {
      organization_id: user.organizationId,
      organization_name: currentOrg?.name,
      warehouse_id: currentWarehouse?._id,
      warehouse_name: currentWarehouse?.name,
    });
    wasIdentified.current = true;
  };

  const unmountPostHogUser = () => {
    if (posthog.__loaded && wasIdentified.current) {
      posthog.reset();
      wasIdentified.current = false;
    }
  };

  useDebouncedEffect(
    () => {
      if (loading) return;

      if (isAuthenticated) {
        loadPostHogUser();
      } else {
        unmountPostHogUser();
      }
    },
    [isAuthenticated, loading],
    500,
  );

  return <>{children}</>;
}
